import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { getInstitutionShortLabel } from '../../utils/institutionAssets';
import { getJupasOrderBandColor, getJupasOrderRankLabel } from '../../utils/jupasOrderDisplay';
import { getProgrammeRankingBarTitle } from '../../utils/programmeDisplay';

export default function JupasSurveyAdmittedConfirmModal({
  open,
  programme,
  rankIndex,
  language,
  onConfirm,
  onCancel,
}) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open || !programme || typeof document === 'undefined') return null;

  const admittedBadgeClass =
    'flex shrink-0 items-center justify-center self-stretch rounded-md bg-emerald-600 px-2 py-1 text-[10px] font-bold leading-tight text-white';

  return createPortal(
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/55 px-4 py-8" role="presentation">
      <section
        className="w-full max-w-lg rounded-2xl border border-edge bg-surface-elevated p-6 text-left shadow-2xl sm:p-7"
        role="dialog"
        aria-modal="true"
        aria-labelledby="jupas-survey-admitted-title"
        aria-describedby="jupas-survey-admitted-description"
      >
        <h2 id="jupas-survey-admitted-title" className="text-xl font-bold text-ink sm:text-2xl">
          {t('jupasListSubmission.admittedConfirm.title')}
        </h2>
        <p id="jupas-survey-admitted-description" className="mt-3 text-sm leading-6 text-ink-muted">
          {t('jupasListSubmission.admittedConfirm.body')}
        </p>

        <div className="mt-4 rounded-xl border border-emerald-200/80 bg-emerald-50/50 p-2 dark:border-emerald-900/40 dark:bg-emerald-950/20">
          <div className="flex select-none items-stretch gap-2 rounded-lg border border-edge bg-surface px-2 py-1.5 shadow-sm">
            <span
              className="flex h-6 w-6 shrink-0 self-center items-center justify-center rounded-full text-[9px] font-bold leading-none text-neutral-900"
              style={{ backgroundColor: getJupasOrderBandColor(rankIndex) }}
            >
              {getJupasOrderRankLabel(rankIndex)}
            </span>
            <div className="flex min-w-0 flex-1 items-stretch gap-2 text-left">
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-center gap-1.5">
                  <span className="shrink-0 text-[9px] font-bold leading-none text-link">
                    {programme.code}
                  </span>
                  <span className="min-w-0 truncate text-[9px] font-medium text-ink-subtle">
                    {getInstitutionShortLabel(programme.institution, language)}
                  </span>
                </div>
                <p className="mt-px truncate text-[11px] font-medium leading-tight text-ink">
                  {getProgrammeRankingBarTitle(programme, language)}
                </p>
              </div>
              <span className={admittedBadgeClass}>
                {t('jupasListSubmission.admittedLabel')}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-2 sm:grid-cols-2">
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('jupasListSubmission.admittedConfirm.confirm')}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-edge-strong px-4 py-2.5 text-sm font-semibold text-ink transition-colors hover:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('jupasListSubmission.admittedConfirm.cancel')}
          </button>
        </div>
      </section>
    </div>,
    document.body
  );
}
